/**
 * Applications + Professionals API client — Story 7.5a (Epic 7 Area C additive).
 *
 * Thin fetch helpers for `routes/applications.ts` + the minimal
 * `routes/professionals.ts`. Cookies included; non-2xx throws.
 */
import { API_BASE } from '@/lib/api'
import { parseProfessionalConflict } from '@/lib/api/professional-errors'
import { readProblemMessage } from '@/lib/api/problem'
import type { EvaluationStatus, ShareState } from '@/lib/api/evaluations'
import type {
  ApplicationReportEmail,
  ApplicationReportEmailResponse,
  BulkApplicationAction,
  BulkApplicationActionResponse,
  BulkCreateApplications,
  BulkCreateApplicationsResponse,
  ClientSignal,
} from '@caliber/shared/dtos'
import {
  professionalListQueryToParams,
  type ProfessionalListQuery,
  type ProfessionalSeniority,
  type ProfessionalSituation,
  type ProfessionalListGroup,
  type ProfessionalListGroupTotal,
  type SkillCoverageResponse,
} from '@caliber/shared'

export type ApplicationStatus = 'active' | 'rejected' | 'hired' | 'withdrawn'

/**
 * O ÚLTIMO sinal do cliente na vitrine — `interested` ou `declined` — e quando
 * foi dado (Story 23.20). `null` = o cliente nunca respondeu.
 *
 * ⚠️ "Último" é decidido no SERVIDOR, e por uma fonte só (`occurred_at DESC,
 * rowid DESC`): dois cliques no mesmo segundo empatam no `occurredAt`, e só o
 * servidor sabe qual veio depois. Não o recalcular aqui a partir dos eventos.
 *
 * Vem na listagem, no detalhe e em cada cartão do quadro.
 */
export interface LatestClientSignal {
  signal: ClientSignal
  at: string
}

/**
 * Avaliação ATIVA de um tipo numa candidatura: a fixada manualmente ou, na
 * ausência dela, a mais recente daquele tipo. `isPinned` diz qual dos dois casos
 * — a UI usa isso para marcar "Ativa (fixada)" vs. "Ativa (mais recente)".
 */
export interface ActiveEvaluation {
  id: string
  status: EvaluationStatus
  score: number | null
  createdAt: string
  isPinned: boolean
}

/** List/detail shape — includes denormalised professional/opportunity names. */
export interface Application {
  id: string
  professionalId: string
  /**
   * ⚠️ `candidateName` É O NOME DO FIO, E FICA — não é um esquecimento.
   *
   * A ponte bilíngue da API espelhava UM campo só (`candidateId` →
   * `professionalId`) e já foi levantada; este NUNCA fez parte dela. Não existe
   * `professionalName` em resposta nenhuma da API — a busca por ele em
   * `apps/api` dá zero.
   *
   * Renomeá-lo aqui compilaria (o corpo é lido com um `as`, não validado) e
   * deixaria a coluna de nomes VAZIA em toda a lista de candidaturas, no quadro
   * e nas avaliações. Só sai no dia em que a API passar a emitir outro nome.
   */
  candidateName: string | null
  opportunityId: string
  opportunityTitle: string | null
  status: ApplicationStatus
  /**
   * A etapa vigente, resolvida a partir do último evento de etapa (W3).
   *
   * `null` significa "ainda sem etapa" — possível quando a vaga não tem processo
   * ou o processo não tem etapa ativa nenhuma. A interface diz isso por extenso
   * em vez de fingir uma etapa que não existe.
   */
  currentStageId: string | null
  currentStageName: string | null
  /**
   * A posição da etapa no processo. É daqui que sai a COR do pill: o espectro
   * nunca dependeu dos nomes das etapas, dependia da ordem (onda W5).
   */
  currentStagePosition: number | null
  /**
   * O processo a que a etapa vigente pertence (Story 23.5). A posição só compara
   * dentro do mesmo processo — é o que a ordenação por Etapa usa para não pôr a
   * etapa 2 de um processo ao lado da 2 de outro.
   */
  currentStagePipelineId: string | null
  /** Desde quando está nesta etapa — o `occurredAt` do evento vigente. */
  currentStageSince: string | null
  /**
   * Arquivada (W3). NÃO é `status: 'withdrawn'`: aquilo é um desfecho real (o
   * profissional desistiu); isto diz só que alguém arrumou a lista.
   */
  archivedAt: string | null
  notes: string | null
  /**
   * Quando esta candidatura foi APRESENTADA na vitrine do cliente. `null` = fora
   * dela. Portal do Cliente, F3.
   *
   * ⚠️ NÃO se escreve por `updateApplication`: o caminho é `setPresented()`
   * (`lib/api/public-profile.ts`), que o servidor RECUSA enquanto o perfil
   * público não estiver `ready`. Se um dia isto entrar num `PATCH` genérico, uma
   * edição de notas passa a poder expor alguém a um cliente.
   *
   * ⚠️ Não confundir com o estado do PERFIL. São dois factos independentes, e as
   * duas combinações existem: perfil `ready` com `presentedAt` nulo (gerado, ainda
   * não apresentado) e — depois de uma regeneração que dispare a trava — perfil
   * `draft` com `presentedAt` preenchido, que a vitrine deixa de servir.
   */
  presentedAt: string | null
  /**
   * A resposta do cliente na vitrine (Story 23.20). Independente de
   * `presentedAt`: um perfil retirado da vitrine guarda o sinal que recebeu.
   */
  clientSignal: LatestClientSignal | null
  createdAt: string
  updatedAt: string
  // Avaliação ativa de cada tipo, ou `null` se ainda não houver nenhuma daquele
  // tipo. Presente TANTO na lista (`GET /api/applications`) quanto no detalhe
  // (`GET /api/applications/:id`) — o detalhe passou a carregá-las para exibir
  // os status de entrevista/prova no cabeçalho.
  activeInterviewEvaluation: ActiveEvaluation | null
  activePracticalEvaluation: ActiveEvaluation | null
  /**
   * O CV que a pessoa mandou PARA ESTA VAGA (migration 0043).
   *
   * ⚠️ NÃO é o mesmo arquivo que os campos homónimos de {@link Professional}.
   * Aquele descreve a PESSOA e vale em todas as vagas; este é o currículo
   * adaptado a esta candidatura — a mesma pessoa apresenta-se de maneiras
   * diferentes a vagas diferentes, e era isso que obrigava a substituir o CV do
   * profissional a cada nova candidatura, perdendo o anterior.
   *
   * Os quatro andam juntos: ou há CV (todos preenchidos) ou não há (todos
   * `null`). `cvName` é o que a tela testa. O `cvR2Key` fica no servidor.
   */
  cvName?: string | null
  cvMime?: string | null
  cvSize?: number | null
  cvUploadedAt?: string | null
}

export interface ApplicationsListResponse {
  items: Application[]
  page: number
  pageSize: number
  total: number
}

export interface CreateApplicationInput {
  professionalId: string
  opportunityId: string
  // Onda W5 — sem `stage`: a candidatura nasce na PRIMEIRA etapa do processo da
  // vaga, e o servidor resolve-a. Sem `status`: nasce sempre "ativa", e mudá-lo
  // é um evento (W3), não um campo de criação.
  notes?: string | null
}

/**
 * ⚠️ `stage` e `status` SAÍRAM daqui na W3. As duas coisas mudam por
 * `createApplicationEvent`, que é o único caminho que grava história com data,
 * hora e autor — e o servidor devolve 400 a quem as mandar por aqui.
 */
export type UpdateApplicationInput = {
  notes?: string | null
  /**
   * Fixa (string) ou desfixa (`null`) a avaliação ativa daquele tipo. Omitir a
   * chave mantém o ponteiro como está — os três estados são distintos, então
   * `null` NÃO pode ser trocado por `undefined` ao montar o body.
   */
  primaryInterviewEvaluationId?: string | null
  primaryPracticalEvaluationId?: string | null
  /**
   * `false` DESARQUIVA — a volta que o `archiveApplication` não tem. É o mesmo
   * campo de `updatePipeline({ archived: false })`: arquivar é um estado, e o
   * inverso de um estado é o mesmo campo com o outro valor.
   */
  archived?: boolean
}

const jsonHeaders = { 'Content-Type': 'application/json', Accept: 'application/json' }

/**
 * GET /api/applications.
 *
 * `archived: true` TROCA a lista pelas arquivadas — não as acrescenta. Sem o
 * parâmetro, o servidor devolve só as não-arquivadas: quem não pede arquivadas
 * não as recebe. O badge da barra lateral conta essas, de TODAS as vagas; desde
 * a Story 23.16 a listagem abre uma vaga de cada vez, e o número deixou de ser
 * o que o link mostra.
 */
export async function listApplications(
  params: {
    opportunityId?: string
    professionalId?: string
    /**
     * As candidaturas às vagas DESTE cliente.
     *
     * ⚠️ Não é uma coluna de `applications`: uma candidatura não conhece o
     * cliente, a VAGA é que o conhece. O servidor resolve o salto (junta
     * `opportunities` e filtra por `customer_id`) porque a alternativa do lado
     * do browser era ler as vagas do cliente e disparar um `GET` por cada uma —
     * N pedidos para desenhar uma lista, e uma lista silenciosamente incompleta
     * quando a página de vagas não chegasse ao fim.
     */
    customerId?: string
    status?: ApplicationStatus
    archived?: boolean
    page?: number
    pageSize?: number
    /**
     * Termo de busca — o servidor procura pelo nome do profissional e pelo título
     * da vaga. Uma candidatura não tem texto próprio: é a junção de duas
     * coisas, e é por essas duas que se procura.
     */
    q?: string
    signal?: AbortSignal
  } = {},
): Promise<ApplicationsListResponse> {
  const url = new URL(`${API_BASE}/applications`, window.location.origin)
  if (params.opportunityId) url.searchParams.set('opportunityId', params.opportunityId)
  if (params.professionalId) url.searchParams.set('professionalId', params.professionalId)
  // Só quando vem — um `customerId` vazio no query string faria o servidor
  // filtrar por um cliente que não existe e devolver zero candidaturas, que é o
  // pior resultado possível: uma lista vazia que parece um cliente sem carteira.
  if (params.customerId) url.searchParams.set('customerId', params.customerId)
  if (params.status) url.searchParams.set('status', params.status)
  if (params.archived) url.searchParams.set('archived', 'true')
  url.searchParams.set('page', String(params.page ?? 1))
  url.searchParams.set('pageSize', String(params.pageSize ?? 50))
  // Só quando há termo — ver a nota igual em `listProfessionals`.
  if (params.q?.trim()) url.searchParams.set('q', params.q.trim())

  const init: RequestInit = { credentials: 'include', headers: { Accept: 'application/json' } }
  if (params.signal) init.signal = params.signal

  const res = await fetch(`${url.pathname}${url.search}`, init)
  if (!res.ok) throw new Error(`Failed to list applications (${res.status})`)
  return (await res.json()) as ApplicationsListResponse
}

export async function getApplication(id: string, signal?: AbortSignal): Promise<Application> {
  const init: RequestInit = { credentials: 'include', headers: { Accept: 'application/json' } }
  if (signal) init.signal = signal
  const res = await fetch(`${API_BASE}/applications/${encodeURIComponent(id)}`, init)
  if (!res.ok) throw new Error(`Failed to load application (${res.status})`)
  return (await res.json()) as Application
}

export async function createApplication(body: CreateApplicationInput): Promise<Application> {
  const res = await fetch(`${API_BASE}/applications`, {
    method: 'POST',
    credentials: 'include',
    headers: jsonHeaders,
    body: JSON.stringify(body),
  })
  if (!res.ok) throw new Error(`Failed to create application (${res.status})`)
  return (await res.json()) as Application
}

export async function updateApplication(
  id: string,
  body: UpdateApplicationInput,
): Promise<Application> {
  const res = await fetch(`${API_BASE}/applications/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: jsonHeaders,
    body: JSON.stringify(body),
  })
  if (!res.ok) throw new Error(`Failed to update application (${res.status})`)
  return (await res.json()) as Application
}

/**
 * DELETE — ARQUIVA (migration 0025, W3).
 *
 * Chamava-se `withdrawApplication` e escrevia `status = 'withdrawn'`. O nome
 * antigo mentia tanto quanto o efeito: "retirada" é uma decisão do CANDIDATO, e
 * usá-la para arrumar a lista afirmava algo sobre ele que ninguém verificou.
 * Agora escreve `archivedAt` e o status fica intacto.
 *
 * A VOLTA é `updateApplication(id, { archived: false })` — não há um
 * `unarchiveApplication` porque não há um endpoint próprio para desarquivar:
 * arquivar é um estado, e o inverso vive no mesmo `PATCH` que os outros campos.
 */
export async function archiveApplication(id: string): Promise<Application> {
  const res = await fetch(`${API_BASE}/applications/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  })
  if (!res.ok) throw new Error(`Failed to archive application (${res.status})`)
  return (await res.json()) as Application
}

/**
 * POST /api/applications/bulk — a MESMA ação sobre várias candidaturas.
 *
 * O servidor responde 200 mesmo quando algumas falham: cada id traz o seu
 * `ok`, pela ordem do pedido. Só um erro do pedido inteiro (400, 401, 5xx) é
 * que lança — uma recusa por linha (`not_found`, `profile_not_ready`) é
 * resultado, não exceção, e quem chama resume-a.
 */
export async function bulkApplicationAction(
  action: BulkApplicationAction['action'],
  ids: string[],
): Promise<BulkApplicationActionResponse> {
  const body: BulkApplicationAction = { action, ids }
  const res = await fetch(`${API_BASE}/applications/bulk`, {
    method: 'POST',
    credentials: 'include',
    headers: jsonHeaders,
    body: JSON.stringify(body),
  })
  if (!res.ok) throw new Error(`Failed to run bulk action (${res.status})`)
  return (await res.json()) as BulkApplicationActionResponse
}

/**
 * POST /api/applications/bulk-create — criar candidaturas numa vaga para os
 * profissionais selecionados na lista (Story 31.9). Um desfecho por profissional;
 * uma vaga que não existe é erro do pedido todo, com a mensagem do servidor.
 */
export async function bulkCreateApplications(
  opportunityId: string,
  professionalIds: string[],
): Promise<BulkCreateApplicationsResponse> {
  const body: BulkCreateApplications = { opportunityId, professionalIds }
  const res = await fetch(`${API_BASE}/applications/bulk-create`, {
    method: 'POST',
    credentials: 'include',
    headers: jsonHeaders,
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    throw new Error(
      await readProblemMessage(res, `Não foi possível criar as candidaturas (${res.status})`),
    )
  }
  return (await res.json()) as BulkCreateApplicationsResponse
}

/**
 * POST /api/applications/report-email — os relatórios de entrevista destas
 * candidaturas (todas da MESMA vaga) num e-mail só, para os e-mails da vaga e
 * com cópia para quem clicou (Story 23.15). Liga o link público de quem ainda
 * não o tinha, e anexa o CV de cada candidatura. O erro traz a frase do
 * servidor.
 */
export async function sendApplicationReportEmail(
  applicationIds: string[],
): Promise<ApplicationReportEmailResponse> {
  const body: ApplicationReportEmail = { applicationIds }
  const res = await fetch(`${API_BASE}/applications/report-email`, {
    method: 'POST',
    credentials: 'include',
    headers: jsonHeaders,
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    throw new Error(await readProblemMessage(res, 'Não foi possível enviar o e-mail.'))
  }
  return (await res.json()) as ApplicationReportEmailResponse
}

// ---- Professionals (minimal) ----

export interface Professional {
  id: string
  name: string
  email: string | null
  githubUsername: string | null
  notes: string | null
  createdAt: string
  // Story 11.1 (Epic 11) — enriched profile fields. OPTIONAL on this shared
  // interface so the existing minimal call sites (hub fixture, create flow, the
  // professionals list) that only carry the base fields keep type-checking. The
  // list endpoint returns these too (full-row select), hence at least `phone`
  // is renderable in the list. Salaries / employment status live here but are
  // never RENDERED in the list or the hub (sensitive — edit page only).
  phone?: string | null
  phoneNormalized?: string | null
  linkedinUrl?: string | null
  city?: string | null
  state?: string | null
  country?: string | null
  expectedSalaryClt?: number | null
  expectedSalaryPj?: number | null
  currentSalary?: number | null
  openToRemote?: boolean | null
  openToHybrid?: boolean | null
  openToOnsite?: boolean | null
  isCurrentlyEmployed?: boolean | null
  currentRole?: string | null
  currentCompany?: string | null
  noticePeriodDays?: number | null
  willingToRelocate?: boolean | null
  preferredContractType?: ContractType | null
  /** Story 31.5 (0056). Opcional mesmo onde o vizinho é obrigatório: as respostas antigas não a trazem. */
  seniority?: ProfessionalSeniority | null
  updatedAt?: string | null
  /**
   * Quantas candidaturas o candidato tem (todas, inclusive retiradas). Devolvido
   * por `GET /api/professionals`; OPCIONAL porque os outros pontos que produzem um
   * `Professional` (create inline, fixtures) não o carregam.
   */
  applicationCount?: number
  /**
   * Quando o profissional foi arquivado (migration 0031), ou `null` se está
   * visível. É o que decide entre "Arquivar" e "Desarquivar" na tela do
   * profissional — sem ele, a tela teria de adivinhar pelo filtro da lista de onde
   * se veio, e adivinharia errado em qualquer chegada direta por URL.
   *
   * OPCIONAL pela mesma razão de `applicationCount`: nem todo ponto que produz
   * um `Professional` o carrega.
   */
  archivedAt?: string | null
  /**
   * As colunas DERIVADAS da listagem (Story 31.1) — só vêm quando a tela as pede
   * em `columns`, porque cada uma é uma subconsulta por linha no servidor.
   *
   * `situation`: onde a pessoa está em relação ao trabalho (ver
   * `PROFESSIONAL_SITUATIONS` em `@caliber/shared`). `currentAllocation`: o
   * cliente e as datas da alocação viva, a em vigor à frente; `null` sem nenhuma.
   * `activeApplicationCount`: candidaturas ativas e não arquivadas.
   *
   * As do contato (Story 31.4), lidas do caderno com o que QUEM PEDE pode ver:
   * `lastContact` e `lastOneOnOne` são instantes ISO, `null` = nunca;
   * `oneOnOneDue` liga em vigor numa alocação e sem 1:1 há mais de 30 dias.
   */
  situation?: ProfessionalSituation
  currentAllocation?: { customerName: string; startedOn: string; endedOn: string | null } | null
  activeApplicationCount?: number
  lastContact?: string | null
  lastOneOnOne?: string | null
  oneOnOneDue?: boolean
  /**
   * Story 31.6 — a função principal (`null` sem nenhuma) e as três skills de nível
   * mais alto; desde a 31.7, as EVIDENCIADAS primeiro — com uma prova de entrevista
   * cuja nota sustenta o nível mostrado —, com o booleano.
   */
  primaryRole?: { areaName: string; roleName: string } | null
  topSkills?: { name: string; level: number; evidenced: boolean }[]
  /** Story 31.8 — TODAS as skills, com o id: as células da matriz. */
  skillLevels?: { skillId: string; name: string; level: number; evidenced: boolean }[]
  /**
   * Story 31.10 — a chave do grupo «cliente atual» desta linha (o nome sem acentos;
   * `null` sem cliente). Só vem numa lista agrupada por cliente; por situação, a chave
   * é a `situation`.
   */
  groupKey?: string | null
  /**
   * O CV EXTERNO — o arquivo que o próprio profissional enviou, anexado a ELE e
   * não a uma candidatura. Nada a ver com `resumes`, o CV que a plataforma
   * gera; ver o cabeçalho de `lib/api/professionals.ts`.
   *
   * Os quatro campos andam juntos: ou há CV (todos preenchidos) ou não há
   * (todos `null`). `cvName` é o que a tela testa — é o único que nunca é
   * vazio num CV que existe. `cvR2Key` também vem na resposta e a UI ignora-o
   * de propósito: a chave do bucket não é assunto do browser.
   *
   * OPCIONAIS pela mesma razão de `archivedAt`: as fixtures e os pontos que
   * criam um `Professional` mínimo não os carregam.
   */
  cvName?: string | null
  cvMime?: string | null
  cvSize?: number | null
  cvUploadedAt?: string | null
  /**
   * A FOTO (migration 0053, Story 24.9). `photoUpdatedAt` é ao mesmo tempo o
   * "há foto?" (é `null` sem ela — as quatro colunas andam juntas) e o
   * cache-buster do `?v=` de `professionalPhotoUrl`. `photoR2Key` também vem na
   * resposta e a UI ignora-o de propósito, como o `cvR2Key`.
   *
   * OPCIONAIS pela mesma razão dos campos do CV.
   */
  photoMime?: string | null
  photoSize?: number | null
  photoUpdatedAt?: string | null
  /** O e-mail corporativo do profissional alocado — o que o cliente vai ver. */
  genesisEmail?: string | null
}

/** Contract type the professional prefers (matches the API enum). */
export type ContractType = 'clt' | 'pj' | 'both'

/**
 * Full professional row from GET /api/professionals/:id — every enriched field is
 * present (non-`undefined`, though many may be `null`). Used by the edit page.
 */
export interface ProfessionalDetail {
  id: string
  name: string
  email: string | null
  githubUsername: string | null
  notes: string | null
  phone: string | null
  phoneNormalized?: string | null
  linkedinUrl: string | null
  city: string | null
  state: string | null
  country: string | null
  expectedSalaryClt: number | null
  expectedSalaryPj: number | null
  currentSalary: number | null
  openToRemote: boolean | null
  openToHybrid: boolean | null
  openToOnsite: boolean | null
  isCurrentlyEmployed: boolean | null
  currentRole: string | null
  currentCompany: string | null
  noticePeriodDays: number | null
  willingToRelocate: boolean | null
  preferredContractType: ContractType | null
  /** Story 31.5 (0056). Opcional mesmo onde o vizinho é obrigatório: as respostas antigas não a trazem. */
  seniority?: ProfessionalSeniority | null
  createdAt: string
  updatedAt: string | null
  /** CV externo — ver o bloco homónimo em {@link Professional}. */
  cvName?: string | null
  cvMime?: string | null
  cvSize?: number | null
  cvUploadedAt?: string | null
  /** Foto e e-mail Genesis — ver os blocos homónimos em {@link Professional}. */
  photoMime?: string | null
  photoSize?: number | null
  photoUpdatedAt?: string | null
  genesisEmail?: string | null
}

/**
 * PATCH body — todo campo é opcional (atualização parcial). Em 2026-07-27 o
 * dono decidiu que nome, email, GitHub e notas passam a ser editáveis por
 * aqui (antes só os campos de perfil enriquecido eram aceitos nesta
 * interface). Um campo omitido aqui é deixado intocado no servidor (preserva
 * um `null` tri-state); um `null` explícito o limpa.
 */
export interface UpdateProfessionalInput {
  name?: string
  email?: string | null
  /** E-mail Genesis (Story 24.9). O servidor valida o formato (400). */
  genesisEmail?: string | null
  githubUsername?: string | null
  notes?: string | null
  phone?: string | null
  linkedinUrl?: string | null
  city?: string | null
  state?: string | null
  country?: string | null
  expectedSalaryClt?: number | null
  expectedSalaryPj?: number | null
  currentSalary?: number | null
  openToRemote?: boolean | null
  openToHybrid?: boolean | null
  openToOnsite?: boolean | null
  isCurrentlyEmployed?: boolean | null
  currentRole?: string | null
  currentCompany?: string | null
  noticePeriodDays?: number | null
  willingToRelocate?: boolean | null
  preferredContractType?: ContractType | null
  /** Story 31.5 (0056). Opcional mesmo onde o vizinho é obrigatório: as respostas antigas não a trazem. */
  seniority?: ProfessionalSeniority | null
  /**
   * `false` DESARQUIVA. É a volta pelo mesmo campo, como nas candidaturas e nos
   * processos seletivos — não há endpoint próprio para desarquivar.
   *
   * NÃO substitui `deleteProfessional`: excluir é para o cadastro sem vínculo
   * nenhum (a exclusão física guardada), arquivar é para quem tem vínculos e
   * por isso o servidor recusa com 409.
   */
  archived?: boolean
}

export interface ProfessionalsListResponse {
  items: Professional[]
  page: number
  pageSize: number
  total: number
  /**
   * Story 31.10 — numa lista agrupada, o agrupamento que a resposta SEGUIU (é por ele
   * que a tela sabe se as linhas à vista são desta consulta ou da anterior) e o total
   * de cada grupo na lista inteira.
   */
  group?: ProfessionalListGroup
  groups?: ProfessionalListGroupTotal[]
}

/**
 * `archived: true` TROCA a lista pelos arquivados — não os acrescenta aos
 * ativos. Mesmo contrato de `listApplications` e `listPipelines`.
 *
 * A consulta (busca, filtros, ordenação, colunas) é escrita pelo MESMO
 * serializador que a API lê (`professionalListQueryToParams`, Story 31.1): os
 * defaults não vão no URL, e um parâmetro novo não precisa de ser ensinado aos
 * dois lados.
 */
export async function listProfessionals(
  params: Partial<
    Pick<
      ProfessionalListQuery,
      'page' | 'pageSize' | 'archived' | 'sort' | 'columns' | 'filters' | 'group'
    >
  > & {
    /**
     * Termo de busca — o servidor procura em nome, email, telefone, GitHub e
     * LinkedIn. Sem ele, o cliente só podia filtrar a página que recebeu, e
     * quem estivesse fora dela era invisível para a busca.
     */
    q?: string
    signal?: AbortSignal
  } = {},
): Promise<ProfessionalsListResponse> {
  const { signal, ...consulta } = params
  const query = new URLSearchParams(
    professionalListQueryToParams({
      ...consulta,
      page: consulta.page ?? 1,
      pageSize: consulta.pageSize ?? 200,
    }),
  ).toString()
  const init: RequestInit = { credentials: 'include', headers: { Accept: 'application/json' } }
  if (signal) init.signal = signal
  const res = await fetch(`${API_BASE}/professionals${query ? `?${query}` : ''}`, init)
  if (!res.ok) throw new Error(`Failed to list professionals (${res.status})`)
  return (await res.json()) as ProfessionalsListResponse
}

/**
 * A COBERTURA das skills na fatia da lista (Story 31.8) — as colunas e o rodapé da
 * matriz. Os mesmos filtros, a mesma busca e o mesmo eixo de arquivo da lista; a
 * página e a ordenação não contam, porque a cobertura é da lista inteira.
 */
export async function getSkillCoverage(
  params: Pick<ProfessionalListQuery, 'archived' | 'q' | 'filters'> & { signal?: AbortSignal },
): Promise<SkillCoverageResponse> {
  const { signal, ...consulta } = params
  const query = new URLSearchParams(professionalListQueryToParams(consulta)).toString()
  const init: RequestInit = { credentials: 'include', headers: { Accept: 'application/json' } }
  if (signal) init.signal = signal
  const res = await fetch(
    `${API_BASE}/professionals/skill-coverage${query ? `?${query}` : ''}`,
    init,
  )
  if (!res.ok) throw new Error(`Failed to load skill coverage (${res.status})`)
  return (await res.json()) as SkillCoverageResponse
}

/** O payload do formulário de cadastro — os mesmos campos da edição, com nome obrigatório. */
export type CreateProfessionalInput = { name: string } & UpdateProfessionalInput

export async function createProfessional(
  body: CreateProfessionalInput,
): Promise<ProfessionalDetail> {
  const res = await fetch(`${API_BASE}/professionals`, {
    method: 'POST',
    credentials: 'include',
    headers: jsonHeaders,
    body: JSON.stringify(body),
  })
  // 409 = email/telefone/LinkedIn já pertencem a outro profissional. O erro tipado
  // leva o conflitante junto, para o formulário apontar o campo e oferecer o link.
  if (res.status === 409) throw await parseProfessionalConflict(res)
  if (!res.ok) throw new Error(`Failed to create professional (${res.status})`)
  return (await res.json()) as ProfessionalDetail
}

/**
 * Exclusão FÍSICA guardada — só passa para o profissional sem vínculo nenhum.
 *
 * Continua a existir depois da migration 0031 (que trouxe o arquivar) porque
 * `email_normalized` é UNIQUE: guardar um cadastro errado como linha invisível
 * prenderia o email da pessoa e impediria o recadastro dela. Quem TEM vínculos
 * arquiva-se — é o que o 409 abaixo passa a oferecer (`canArchive`).
 */
export async function deleteProfessional(id: string): Promise<void> {
  const res = await fetch(`${API_BASE}/professionals/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  })
  // 409 = há candidatura, avaliação ou CV vinculados; o erro carrega quais e
  // diz se o arquivamento é a saída.
  if (res.status === 409) throw await parseProfessionalConflict(res)
  if (!res.ok) throw new Error(`Failed to delete professional (${res.status})`)
}

// ---- Professional Hub (Story 7.8 — aggregated view) ----

/** Avaliação ativa como o hub a devolve — inclui nome do assessment e transcrição. */
export interface HubActiveEvaluation {
  id: string
  applicationId: string
  assessmentId: string
  assessmentName: string | null
  type: 'interview' | 'practical'
  status: EvaluationStatus
  score: number | null
  hasTranscript: boolean
  createdAt: string
  completedAt: string | null
  isPinned: boolean
}

/**
 * Hub do profissional.
 *
 * Deixou de trazer `resumes` (o card "CVs gerados" saiu do detalhe do profissional
 * e ninguém mais lia o campo). O card "Avaliações" também saiu da tela, mas
 * `evaluations` PERMANECE no contrato: é o que alimenta a mini-timeline de
 * histórico no detalhe da avaliação, que precisa de todas as avaliações do
 * profissional, não só as ativas. O que a tela do profissional mostra agora é a
 * entrevista e a prova ATIVAS dentro de cada candidatura.
 */
export interface ProfessionalHub {
  /**
   * ⚠️ `candidate` É A CHAVE DO ENVELOPE NO FIO, E FICA.
   *
   * `GET /api/professionals/:id/hub` responde `c.json({ candidate, ... })`
   * (`apps/api/src/routes/professionals.ts`). A ponte bilíngue espelhava
   * `candidateId` e mais nada — esta chave nunca fez parte dela, e sobreviveu ao
   * levantamento. Renomeá-la para `professional` daria `undefined` e a tela da
   * pessoa ficaria em branco, sem erro de tipo nenhum.
   *
   * O TIPO já fala a língua nova (`Professional`); a CHAVE é do servidor, e só
   * muda no dia em que o servidor a mudar.
   */
  candidate: Professional
  applications: Array<{
    id: string
    opportunityId: string
    opportunityTitle: string | null
    // Onda W5 — o NOME da etapa vigente, resolvido pelo servidor a partir do
    // ponteiro. `null` em quem ainda não tem etapa.
    currentStageName: string | null
    status: ApplicationStatus
    /**
     * ADR-013 OQ-4 — o hub MOSTRA as candidaturas arquivadas (é "tudo sobre
     * esta pessoa", e é a contagem que arma o diálogo de exclusão), mas a tela
     * tem de as poder distinguir. Sem esta data, mostrar tudo seria misturar
     * tudo. O servidor manda-as no fim da lista.
     */
    archivedAt: string | null
    createdAt: string
    activeInterviewEvaluation: HubActiveEvaluation | null
    activePracticalEvaluation: HubActiveEvaluation | null
  }>
  evaluations: Array<{
    id: string
    applicationId: string
    assessmentId: string
    assessmentName: string | null
    type: 'interview' | 'practical'
    status: EvaluationStatus
    score: number | null
    hasTranscript: boolean
    createdAt: string
    completedAt: string | null
  }>
}

export async function getProfessionalHub(
  id: string,
  signal?: AbortSignal,
): Promise<ProfessionalHub> {
  const init: RequestInit = { credentials: 'include', headers: { Accept: 'application/json' } }
  if (signal) init.signal = signal
  const res = await fetch(`${API_BASE}/professionals/${encodeURIComponent(id)}/hub`, init)
  if (!res.ok) throw new Error(`Failed to load professional hub (${res.status})`)
  return (await res.json()) as ProfessionalHub
}

// ---- Professional profile edit (Story 11.1, Epic 11) ----

/** GET /api/professionals/:id — full professional row for the edit page. */
export async function getProfessionalById(
  id: string,
  signal?: AbortSignal,
): Promise<ProfessionalDetail> {
  const init: RequestInit = { credentials: 'include', headers: { Accept: 'application/json' } }
  if (signal) init.signal = signal
  const res = await fetch(`${API_BASE}/professionals/${encodeURIComponent(id)}`, init)
  if (!res.ok) throw new Error(`Failed to load professional (${res.status})`)
  const body = (await res.json()) as { candidate: ProfessionalDetail }
  return body.candidate
}

/** PATCH /api/professionals/:id — partial profile update. Returns the full row. */
export async function updateProfessional(
  id: string,
  data: UpdateProfessionalInput,
): Promise<ProfessionalDetail> {
  const res = await fetch(`${API_BASE}/professionals/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    credentials: 'include',
    headers: jsonHeaders,
    body: JSON.stringify(data),
  })
  if (res.status === 409) throw await parseProfessionalConflict(res)
  if (!res.ok) throw new Error(`Failed to update professional (${res.status})`)
  const body = (await res.json()) as { candidate: ProfessionalDetail }
  return body.candidate
}

// ---- Partilha pública por CANDIDATURA (migration 0021) ----
//
// O link deixou de pertencer a uma avaliação. Um token por candidatura, estável,
// que resolve sempre a avaliação vigente: reprocessar passa a atualizar o que o
// destinatário vê, em vez de emitir um link novo e deixar o antigo a mostrar o
// resultado velho. O contrato de resposta é o mesmo `ShareState` do share por
// avaliação — importado, não duplicado.

/** GET /api/applications/:id/share — estado atual da partilha. */
export async function getApplicationShareState(
  id: string,
  signal?: AbortSignal,
): Promise<ShareState> {
  const init: RequestInit = { credentials: 'include', headers: { Accept: 'application/json' } }
  if (signal) init.signal = signal
  const res = await fetch(`${API_BASE}/applications/${encodeURIComponent(id)}/share`, init)
  if (!res.ok) throw new Error(`Failed to load share state (${res.status})`)
  return (await res.json()) as ShareState
}

/** POST /api/applications/:id/share — liga a partilha e gera um token novo. */
export async function enableApplicationShare(id: string): Promise<ShareState> {
  const res = await fetch(`${API_BASE}/applications/${encodeURIComponent(id)}/share`, {
    method: 'POST',
    credentials: 'include',
    headers: jsonHeaders,
  })
  if (!res.ok) throw new Error(`Failed to enable share (${res.status})`)
  return (await res.json()) as ShareState
}

/** DELETE /api/applications/:id/share — desliga a partilha (guarda o histórico). */
export async function disableApplicationShare(id: string): Promise<ShareState> {
  const res = await fetch(`${API_BASE}/applications/${encodeURIComponent(id)}/share`, {
    method: 'DELETE',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  })
  if (!res.ok) throw new Error(`Failed to disable share (${res.status})`)
  return (await res.json()) as ShareState
}

// ============================================================
// EVENTOS — a história da candidatura (migration 0025, onda W3)
// ============================================================

/**
 * Um facto na história da candidatura.
 *
 * `type` discrimina o que a linha diz: `'stage'` traz `stageId`/`stageName`,
 * `'status'` traz `status`, `'client_signal'` traz `clientSignal`. Os nomes vêm
 * resolvidos pelo servidor (LEFT JOIN) para a timeline não fazer uma chamada
 * por linha só para escrever uma palavra.
 *
 * `createdByName` pode ser `null` — é o caso dos eventos SEMEADOS pelo backfill
 * da 0025: ninguém os fez, são inferidos do estado anterior. A interface diz
 * isso por extenso em vez de inventar um nome.
 *
 * ⚠️ E É O CASO DE TODO `'client_signal'`: quem o escreveu foi o cliente, que
 * não é um usuário da Genesis (`createdBy: null`, literal no `insert`). O autor
 * de um sinal lê-se pelo `type`, nunca pelo `createdByName`.
 */
export interface ApplicationEvent {
  id: string
  applicationId: string
  type: 'stage' | 'status' | 'client_signal'
  stageId: string | null
  stageName: string | null
  status: ApplicationStatus | null
  /** Só em `type: 'client_signal'` (Story 23.20); `null` nos outros. */
  clientSignal: ClientSignal | null
  /** Num sinal, só o portal a escreve — o catálogo público não aceita nota. */
  note: string | null
  createdBy: string | null
  createdByName: string | null
  /** QUANDO o facto aconteceu (pode ser retroativo). */
  occurredAt: string
  /** Quando foi REGISTADO. Diverge de `occurredAt` no backfill e nos lançamentos retroativos. */
  createdAt: string
}

export type CreateApplicationEventInput =
  | { type: 'stage'; stageId: string; note?: string | null; occurredAt?: string }
  | { type: 'status'; status: ApplicationStatus; note?: string | null; occurredAt?: string }

/** GET /api/applications/:id/events — do mais recente para o mais antigo. */
export async function listApplicationEvents(
  id: string,
  signal?: AbortSignal,
): Promise<ApplicationEvent[]> {
  const res = await fetch(`${API_BASE}/applications/${encodeURIComponent(id)}/events`, {
    credentials: 'include',
    ...(signal ? { signal } : {}),
  })
  if (!res.ok) {
    throw new Error(
      await readProblemMessage(res, `Não foi possível carregar o histórico (${res.status})`, [
        'detail',
        'title',
      ]),
    )
  }
  const body = (await res.json()) as { items: ApplicationEvent[] }
  return body.items
}

/**
 * POST /api/applications/:id/events — mover de etapa, ou mudar o status.
 *
 * Os 409 desta rota (candidatura encerrada, etapa arquivada) trazem uma frase
 * que diz o que fazer a seguir, e a interface mostra-a tal como veio — daí não
 * haver classe de erro tipada aqui, à semelhança da API de pipelines.
 */
export async function createApplicationEvent(
  id: string,
  body: CreateApplicationEventInput,
): Promise<Application> {
  const res = await fetch(`${API_BASE}/applications/${encodeURIComponent(id)}/events`, {
    method: 'POST',
    credentials: 'include',
    headers: jsonHeaders,
    body: JSON.stringify(body),
  })
  if (!res.ok) {
    throw new Error(
      await readProblemMessage(res, `Não foi possível registrar o evento (${res.status})`, [
        'detail',
        'title',
      ]),
    )
  }
  return (await res.json()) as Application
}
